import Promise from 'bluebird';
import _ from 'underscore';
import { useEffect, useState } from 'react';
import $ from 'jquery';
import { keepAliveInstance } from '@bingads-webui/keep-alive';
// import { versionCheckInstance } from '@bingads-webui-campaign/version-check';
import { setupClarity } from '@bingads-webui-universal/clarity';
import { PageReady } from '@bingads-webui/page-ready';
import { setupQualtricsWithData } from '@bingads-webui-campaign/qualtrics';
import { PreferenceService } from '@bingads-webui/preference-service';
import { initPDL } from './omniture';
import { redirectToLogin, CLARITY_IDS, isTestEnvironment, TEST_CLARITY_IDS, isSignupRunningProxy } from './constants';
import { checkUserQueryParams, deleteQueryParam } from './query-param-checker';

const isTestEnv = isTestEnvironment(document.domain);
const CookieConsentActivityName = 'wcp-consent';

export function setupServiceRoots(serverContext) {
  // we check for proxy running so that we can properly route cc API calls in proxy
  if (isSignupRunningProxy()) {
    // eslint-disable-next-line no-param-reassign
    serverContext.ServicesEndpoints.ClientCenterUIAPI = 'https://app1.ads-int.microsoft.com:12000/cc/';
  }
}

function getCLarityID(feature) {
  return isTestEnv ? TEST_CLARITY_IDS[feature] : CLARITY_IDS[feature];
}

/**
 * Initialize global app services.
 * @param {any} {
 *     featureName - Name of feature
 *     serverContext - Context needed for initializing libraries
 *     shouldLoadAdobe - Determines if Adobe tracking should load.
 *     shouldStartKeepAlive - Determines if keep-alive polling should start.
 *     shouldSetQueryParams - Determines if script to update url with query params will run
 *     shouldSetupQualtrics - Determines if qualtrics initialization should run.
 *     shouldEnableUnAuth - Determines if should not redirect when un auth
 *     shouldLoadAmplifyTracking - Determines if Amplify tracking script should load.
 *   }
 * @returns {void}
 */
export function initGlobalService({
  featureName,
  serverContext,
  shouldLoad1DS = true,
  shouldLoadAdobe = false,
  shouldStartKeepAlive = true,
  shouldSetQueryParams = false,
  shouldSetupQualtrics = false,
  shouldEnableUnAuth = false,
  shouldLoadAmplifyTracking = false,
  shouldSetupQualtricsWithData = false,
  setUpClarityBasedOnUserConsent = false,
  appActivity = {
    trace: _.noop,
    error: _.noop,
  },
}) {
  try {
    // remove skipcccache from URL, this should only be used once to refresh sever cache
    deleteQueryParam('skipcccache');

    if (!shouldEnableUnAuth) {
      // create callback that will sign out if user's auth token is invalid or expired
      keepAliveInstance.unAuthCallBack = () => redirectToLogin(serverContext);
    }

    if (shouldStartKeepAlive) {
      // start keep alive
      keepAliveInstance.start();
    }

    /*
    TODO: implement
    // start version check
    versionCheckInstance.version = serverContext.Version;
    versionCheckInstance.start();
    */

    if (shouldSetQueryParams) {
      // if user put wrong values for known query params (uid, cid, aid)
      // restores it per server context
      checkUserQueryParams(serverContext);
    }

    // init an empty function to avoid customer click the help icon
    // before the apex loading is ready
    if (!window.PopupController) {
      window.PopupController = { PopupJsClick: () => { } };
    }
    PageReady.afterPageReady().then(() => {
      if (shouldSetupQualtricsWithData) {
        setupQualtricsWithData(serverContext, {
          PreferenceService,
          enableIframeCommunication: true,
          appActivity,
        });
      } else if (shouldSetupQualtrics) {
        setupQualtricsWithData(serverContext);
      }

      const init1DSAnalytics = () => {
        const scriptUrl = 'https://js.monitor.azure.com/scripts/c/ms.analytics-web-3.min.js';

        if (!_.isFunction($.ajax)) {
          return new Promise();
        }

        return new Promise((resolve) => {
          $.ajax({
            url: scriptUrl,
            dataType: 'script',
            crossOrigin: true,
            credentials: 'omit',
          }).always(() => resolve());
        });
      };

      const initAdobeTracking = () => {
        const trackingScriptUrl = _.chain(serverContext)
          .result('ThirdPartConfig')
          .result('AdobeTrackingScriptUrl', undefined)
          .value();
        if (!_.isUndefined(trackingScriptUrl) && _.isFunction($.getScript)) {
          $.ajax({
            url: trackingScriptUrl,
            dataType: 'script',
            crossOrigin: true,
            credentials: 'omit',
            success: () => initPDL(serverContext),
          });
        }
      };

      const initAmplifyTracking = () => {
        const amplifyTrackingScriptUrl = _.chain(serverContext)
          .result('AmplifyTrackingScriptUrl', undefined)
          .value();
        if (!_.isUndefined(amplifyTrackingScriptUrl) && _.isFunction($.getScript)) {
          $.ajax({
            url: amplifyTrackingScriptUrl,
            dataType: 'script',
            crossOrigin: true,
            credentials: 'omit',
          });
        }
      };

      const clarityId = getCLarityID(featureName);

      if (clarityId) {
        const currentUserId = (serverContext.CurrentUser &&
          serverContext.CurrentUser.Id &&
          serverContext.CurrentUser.Id.toString()) || '';
        const customerId = (serverContext.CurrentCustomer &&
          serverContext.CurrentCustomer.Id &&
          serverContext.CurrentCustomer.Id.toString()) || '';
        const accountId = (serverContext.CurrentAccount &&
          serverContext.CurrentAccount.Id &&
          serverContext.CurrentAccount.Id.toString()) || '';

        const initClarity = () => {
          setupClarity(
            clarityId,
            {
              customerId,
              accountId,
              SessionId: serverContext.Omniture.SessionId,
              PageTrackingId: serverContext.PageTrackingId,
              subFeature: featureName,
            },
            currentUserId,
            serverContext.Omniture.SessionId,
            serverContext.PageTrackingId
          );
        };

        const onConsentChanged = (categoryPreferences) => {
          appActivity.trace(`Consent: ${JSON.stringify(categoryPreferences)}`);
          window.dispatchEvent(new CustomEvent('ConsentChanged', {
            detail: categoryPreferences,
          }));
        };

        if (setUpClarityBasedOnUserConsent || shouldLoadAdobe || shouldLoadAmplifyTracking) {
          const script = document.createElement('script');
          script.src = 'https://wcpstatic.microsoft.com/mscc/lib/v2/wcp-consent.js';
          script.async = true;
          script.onload = () => {
            appActivity.trace('WcpConsent: script loaded', CookieConsentActivityName);
            // Init WcpConsent before in init clarity
            // cookie-banner will be hidden after user sign in, we only use this element to init WCP
            // we depend on CCUI page to show cookie banner to users
            if (window.WcpConsent) {
              appActivity.trace('WcpConsent: initialize start', CookieConsentActivityName);
              window.WcpConsent.init(serverContext.CurrentLocale, 'cookie-banner', (err, _siteConsent) => {
                appActivity.trace('WcpConsent: initialize complete', CookieConsentActivityName);
                if (!err) {
                  window.siteConsent = _siteConsent;
                  appActivity.trace('WcpConsent: initialize succeed', CookieConsentActivityName);
                  if (!window.WcpConsent
                    || !window.WcpConsent.siteConsent
                    || !window.WcpConsent.siteConsent.onConsentChanged
                    || !window.WcpConsent.siteConsent.getConsent
                  ) {
                    if (appActivity.error) {
                      appActivity.error('WcpConsent: WcpConsent component is not initialized. Cannot read user cookie consent info');
                    }
                    return;
                  }
                  onConsentChanged(window.WcpConsent.siteConsent.getConsent());
                  appActivity.trace('WcpConsent: read cookie consent value to see if we can init clarity', CookieConsentActivityName);

                  const initAnalytics = ({ Analytics, Advertising }) => {
                    if (Analytics) {
                      const load1DS = shouldLoad1DS ? init1DSAnalytics : () => Promise.resolve();

                      load1DS().then(() => {
                        if (shouldLoadAdobe) {
                          initAdobeTracking();
                        }
                      });

                      if (shouldLoadAmplifyTracking) {
                        initAmplifyTracking();
                      }
                      if (setUpClarityBasedOnUserConsent && Advertising) {
                        initClarity();
                      }
                    }
                  };

                  const currentConsent = window.WcpConsent.siteConsent.getConsent();

                  if (currentConsent.Analytics) {
                    initAnalytics(currentConsent);
                  } else {
                    window.WcpConsent.siteConsent.onConsentChanged(initAnalytics);
                  }
                } else if (appActivity.error) {
                  appActivity.error(`WcpConsent: initialize error: ${err}`);
                }
              }, onConsentChanged);
            } else {
              appActivity.trace('WcpConsent: wcp-consent.js script has not been loaded yet', CookieConsentActivityName);
            }
          };
          script.onerror = () => {
            // ad blocker might block this script, so we only log trace here.
            appActivity.trace('WcpConsent: WcpConsent script didn\'t load correctly.');
          };

          document.head.appendChild(script);
        } else {
          appActivity.trace('WcpConsent: WcpConsent logic skipped.');
          initClarity();
        }
      } else {
        appActivity.error('init global service error');
      }
    });

    setupServiceRoots(serverContext);
  } catch (e) {
    appActivity.error(`init global service raise exception ${e}`);
  }
}

export const useWcpConstent = () => {
  const [wcpConsent, setWcpConsent] = useState({});
  const onConsentChanged = () => {
    const { siteConsent } = window;
    setWcpConsent(siteConsent);
  };
  useEffect(() => {
    window.addEventListener('ConsentChanged', () => {
      onConsentChanged();
    });
    return () => {
      window.removeEventListener('ConsentChanged', onConsentChanged);
    };
  });

  return wcpConsent;
};
